import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import supabase from '../supabaseClient';
import './AdminPanel.css';

const AdminPanel = () => {
  const location = useLocation();
  const [stats, setStats] = useState({
    specialists: 0,
    users: 0,
    recommendations: 0,
    newRequests: 0,
    isLoading: true,
    error: null
  });

  // Загрузка статистики
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [
          { count: specialists },
          { count: users },
          { count: recommendations },
          { count: newRequests }
        ] = await Promise.all([
          supabase.from('new_specs').select('*', { count: 'exact' }),
          supabase.from('new_users').select('*', { count: 'exact' }),
          supabase.from('new_recs').select('*', { count: 'exact' }),
          supabase.from('admin_requests').select('*', { count: 'exact' }).eq('is_active', true)
        ]);

        setStats({
          specialists: specialists || 0,
          users: users || 0,
          recommendations: recommendations || 0,
          newRequests: newRequests || 0,
          isLoading: false,
          error: null
        });
      } catch (err) {
        setStats(prev => ({
          ...prev,
          isLoading: false,
          error: err.message
        }));
      }
    };

    fetchStats();
  }, []);

  // Скролл к контенту при смене раздела
  useEffect(() => {
    const contentEl = document.querySelector('.admin-content');
    if (contentEl) {
      contentEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.pathname]);

  const menuItems = [
    { path: 'analytics', icon: '📊', label: 'Аналитика' },
    { path: 'enhanced-requests', icon: '🆕', label: 'Новые специалисты' },
    { path: 'logs', icon: '📋', label: 'Логи действий' },
    { path: 'add-stories', icon: '📱', label: 'Добавить сторис' },
    { path: 'video-stories', icon: '🎬', label: 'Видео-сторис' },
    { path: 'all-specialists', icon: '👨‍⚕️', label: 'Все специалисты' },
    { path: 'all-users', icon: '👥', label: 'Все пользователи' },
    { path: 'faq', icon: '❓', label: 'Частые вопросы' },
    { path: 'promo', icon: '🖥️', label: 'Промо-страница' },
    { path: 'documentation', icon: '📚', label: 'Документация' },
    { path: 'invites', icon: '✉️', label: 'Приглашения' },
  ];

  return (
    <div className="admin-container">
      <h1>Админ-панель</h1>
      
      {/* Блок статистики */}
      <div className="admin-stats-container">
        {stats.isLoading ? (
          <div className="admin-stats-loading">Загрузка статистики...</div>
        ) : stats.error ? (
          <div className="admin-stats-error">Ошибка: {stats.error}</div>
        ) : (
          <div className="admin-stats-grid">
            <StatCard 
              title="Специалисты" 
              value={stats.specialists} 
              icon="👨‍⚕️" 
            />
            <StatCard 
              title="Пользователи" 
              value={stats.users} 
              icon="👥" 
            />
            <StatCard 
              title="Рекомендации" 
              value={stats.recommendations} 
              icon="💬" 
            />
            <StatCard 
              title="Новые заявки" 
              value={stats.newRequests} 
              icon="🆕"
              isHighlighted={stats.newRequests > 0} 
            />
          </div>
        )}
      </div>

      <nav className="admin-menu">
        {menuItems.map((item) => (
          <Link 
            key={item.path} 
            to={item.path} 
            className={`admin-menu-item ${location.pathname.includes(item.path) ? 'active' : ''}`}
          >
            <span className="admin-menu-icon">{item.icon}</span>
            <span className="admin-menu-label">{item.label}</span>
          </Link>
        ))}
      </nav>
      
      <div className="admin-content">
        <Outlet />
      </div>
    </div>
  );
};

// Компонент карточки статистики
const StatCard = ({ title, value, icon, isHighlighted = false }) => {
  return (
    <div className={`admin-stat-card ${isHighlighted ? 'highlighted' : ''}`}>
      <div className="admin-stat-icon">{icon}</div>
      <div className="admin-stat-value">{value}</div>
      <div className="admin-stat-title">{title}</div>
    </div>
  );
};

export default AdminPanel;